import React, { Dispatch, SetStateAction, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useFetchDMSBoxMapping, useUserProfile } from '../../hooks/queries';
import styled from 'styled-components';
import { IOktaContext } from '@okta/okta-react/bundles/types/OktaContext';
import { useOktaAuth } from '@okta/okta-react';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import { useTheme } from '../../contexts/Theme';

import {
    Dialog,
    Loader,
    SearchInput,
    INPUT_SIZES,
    TableContent,
    TableContainer,
    TableHeader,
    Checkbox,
    CHECKBOX_SIZES
} from 'cdk-radial';
import { CNumber } from '../../types';
import { driveLaunchFallback } from '../../util/driveLaunchFallback';
import { getDMSProxyFirewallUrl } from '../../util/getDMSProxyUrls';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
    ColDef,
    FirstDataRenderedEvent,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    SelectionChangedEvent
} from 'ag-grid-community';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';

// Harness multivariate flag (Number variation, for example 5).
// Missing / null / empty / invalid / below 1 / not loaded yet -> DEFAULT_SELECTION_LIMIT.
const MULTIPLE_DMS_SELECTION_LIMIT_FLAG = 'UNIFY_MULTI_DMS_SELECTION_LIMIT';
const DEFAULT_SELECTION_LIMIT = 1;

const StyledDMSTitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledDialog = styled(Dialog)`
  width: 60%;
`;

const StyledWithLoader = styled.div`
  justify-content: center;
`;

export const StyledSearchBox = styled(SearchInput)`
  width: 60%;
  left: 1px;
  top: -6px;
  & > input {
    height: 32px;
  }
`;

export const StyledSearchBoxContainer = styled.div`
 display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.5rem 0rem 0rem;
  padding-left: 15px;
  height: auto;
`;

interface IMultipleDMSDialog {
    shouldDialogOpen: boolean;
    setDialogOpen: Dispatch<SetStateAction<boolean>>;
    dmsCNumbers: CNumber[];
    isFetching: boolean;
}

interface RowData {
    ['data-testid']?: string;
    label: string;
    value: string; // unique per row, used as the AG Grid row id
    link: string;
    dmsUserId?: string;
    dmsBoxAliasName?: string;
}

const DEFAULT_COL_DEF: ColDef = { filter: true, sortable: true };
const ROW_HEIGHT = 32;
const HEADER_HEIGHT = 36;
const ROWS_TO_SHOW = 10;
const TABLE_CONTENT_HEIGHT = (ROW_HEIGHT + 4) * ROWS_TO_SHOW;

const getRowId = (params: GetRowIdParams<RowData>): string => String(params.data.value);

const parseSelectionLimit = (value: unknown): number => {
    if (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) {
        const n = Math.floor(Number(value));
        if (Number.isFinite(n) && n >= 1) return n;
    }
    return DEFAULT_SELECTION_LIMIT;
};

const parseSavedValues = (raw: string | null): string[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.filter((v): v is string => typeof v === 'string');
        }
        return typeof parsed === 'string' ? [parsed] : [];
    } catch {
        return [];
    }
};

const DMSDialogTitle = (): JSX.Element => {
    const { t } = useTranslation();
    return (
        <StyledDMSTitleContainer>
            <div>
                {t('multipleDMSDialog.heading')}
            </div>
        </StyledDMSTitleContainer>
    );
};

export const MultipleDMSDialog = ({ shouldDialogOpen, setDialogOpen, dmsCNumbers, isFetching }: IMultipleDMSDialog): JSX.Element => {
    const [isLaunchingDrive, setIsLaunchingDrive] = useState<boolean>(false);
    const { data: userProfileData } = useUserProfile();
    const { darkMode } = useTheme();
    const [selectedDMS, setSelectedDMS] = useState<RowData[]>([]);
    const [searchText, setSearchText] = useState<string>('');

    const session: IOktaContext = useOktaAuth();
    const { t } = useTranslation();
    const { data: dmsBoxes } = useFetchDMSBoxMapping(true);
    const authToken = get(session, 'authState.idToken.idToken', '');
    const { trackEvent } = useAnalytics();
    const userLocale = localStorage.getItem('userLocale') || 'en_US';

    const rawSelectionLimit: unknown = useFeatureFlag(MULTIPLE_DMS_SELECTION_LIMIT_FLAG);
    const selectionLimit = parseSelectionLimit(rawSelectionLimit);
    // Limit 1 -> AG Grid "single" (picking another row replaces the current one, same as prod).
    // Limit 2+ -> "multiple" with the cap enforced in handleSelectionChanged.
    const isMulti = selectionLimit > 1;

    const email = userProfileData?.userProfile?.email || '';
    const storageKeys = useMemo(
        () => ({ flag: `rememberDMS_${email}`, dms: `rememberedDMS_${email}` }),
        [email]
    );

    const [rememberMe, setRememberMe] = useState<boolean>(
        () => localStorage.getItem(storageKeys.flag) === 'true'
    );

    const gridApiRef = useRef<GridApi<RowData> | null>(null);
    // Accepted selection in the order the user picked the rows.
    const selectionOrderRef = useRef<string[]>([]);
    const isRestoringRef = useRef<boolean>(false);

    const hasDmsBoxes = Boolean(dmsBoxes);

    // Same as before: no rows until the DMS box mapping has loaded.
    const rowData = useMemo<RowData[]>(() => {
        if (!dmsBoxes || !dmsCNumbers) return [];
        const rows: RowData[] = dmsCNumbers.map((cNumber) => {
            const dmsBoxAliasName = dmsBoxes?.dmsBoxMappings
                ?.find((eachBoxDetails) => (eachBoxDetails?.cnumber as string)?.toLowerCase() === (cNumber.cNumber).toLowerCase())
                ?.alias || '';

            return {
                'data-testid': cNumber.cNumber,
                label: `${dmsBoxAliasName} [${cNumber.cNumber}]`,
                value: cNumber.cNumber,
                link: `https://${cNumber.cNumber}-dms.drive.connectcdk.com/apps/autoTools/cds/osd/osd.php`,
                dmsUserId: cNumber.dmsUserId,
                dmsBoxAliasName: dmsBoxAliasName.toLowerCase()
            };
        });
        rows.sort((a, b) => (a.dmsBoxAliasName || '').localeCompare(b.dmsBoxAliasName || ''));
        return rows;
    }, [dmsBoxes, dmsCNumbers]);

    const LinkRenderer = useCallback(
        (props: ICellRendererParams<RowData, string>) => (
            <a
                href={props.value}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.stopPropagation();
                    trackEvent(AMPLITUDE_EVENTS.OPEN_ADAPTIVA_OSD_MULTI_DMS_USER, {
                        'C-Number': props.data?.value,
                        'Name': props.data?.label,
                    });
                }}
            >
                {'Adaptiva OSD'}
            </a>
        ),
        [trackEvent]
    );

    const columnDefs = useMemo<ColDef<RowData>[]>(() => {
        if (!hasDmsBoxes) return [];
        return [
            {
                headerName: '',
                checkboxSelection: true,
                headerCheckboxSelection: isMulti,
                headerCheckboxSelectionFilteredOnly: true,
                width: 50,
                minWidth: 50,
                maxWidth: 50,
                sortable: false,
                filter: false,
                resizable: false,
                suppressMenu: true
            },
            { field: 'label', headerName: 'DMS', flex: 1 },
            {
                field: 'link',
                headerName: 'Adaptiva Online Software Distribution',
                cellRenderer: LinkRenderer,
                getQuickFilterText: () => '', // keep URLs out of the search
                flex: 1
            }
        ];
    }, [LinkRenderer, isMulti, hasDmsBoxes]);

    // Fires once per grid instance (first mount, or after the single <-> multiple switch).
    const handleFirstDataRendered = useCallback(
        (e: FirstDataRenderedEvent<RowData>) => {
            const saved = rememberMe ? parseSavedValues(localStorage.getItem(storageKeys.dms)) : [];
            const restored: string[] = [];
            saved.forEach((v) => {
                if (restored.length < selectionLimit && !restored.includes(v) && e.api.getRowNode(v)) {
                    restored.push(v);
                }
            });

            isRestoringRef.current = true;
            try {
                restored.forEach((v) => e.api.getRowNode(v)?.setSelected(true));
            } finally {
                isRestoringRef.current = false;
            }

            selectionOrderRef.current = restored;
            setSelectedDMS(
                restored
                    .map((v) => e.api.getRowNode(v)?.data)
                    .filter((row): row is RowData => !!row)
            );

            if (rememberMe && saved.length > 0 && JSON.stringify(restored) !== JSON.stringify(saved)) {
                if (restored.length > 0) {
                    localStorage.setItem(storageKeys.dms, JSON.stringify(restored));
                } else {
                    localStorage.removeItem(storageKeys.dms);
                }
            }
        },
        [rememberMe, selectionLimit, storageKeys]
    );

    // Enforces the cap. A click (or header "select all") that would go over the limit is undone:
    // rows already selected are never touched, only the newly added ones beyond the limit.
    const handleSelectionChanged = useCallback(
        (e: SelectionChangedEvent<RowData>) => {
            if (isRestoringRef.current) return;
            const api = e.api;

            const selectedValues = new Set<string>(api.getSelectedRows().map((row) => row.value));
            const previous = selectionOrderRef.current;
            const previousSet = new Set<string>(previous);

            // Rows that were already selected keep their place.
            const kept = previous.filter((v) => selectedValues.has(v));

            // Newly selected rows, in the order they are shown on screen.
            const added: string[] = [];
            api.forEachNodeAfterFilterAndSort((node) => {
                const v = node.data?.value;
                if (v !== undefined && selectedValues.has(v) && !previousSet.has(v)) {
                    added.push(v);
                }
            });
            selectedValues.forEach((v) => {
                if (!previousSet.has(v) && !added.includes(v)) added.push(v);
            });

            const finalOrder = [...kept, ...added].slice(0, selectionLimit);
            const finalSet = new Set<string>(finalOrder);
            const rejected = Array.from(selectedValues).filter((v) => !finalSet.has(v));

            if (rejected.length > 0) {
                // Deselecting re-fires this handler with a valid selection.
                rejected.forEach((v) => api.getRowNode(v)?.setSelected(false));
                return;
            }

            const unchanged =
                finalOrder.length === previous.length && finalOrder.every((v, i) => v === previous[i]);
            if (unchanged) return;

            selectionOrderRef.current = finalOrder;
            setSelectedDMS(
                finalOrder
                    .map((v) => api.getRowNode(v)?.data)
                    .filter((row): row is RowData => !!row)
            );
            if (rememberMe) {
                localStorage.setItem(storageKeys.dms, JSON.stringify(finalOrder));
            }
        },
        [selectionLimit, rememberMe, storageKeys]
    );

    // Harness value lowered while the dialog is open: keep the earliest-picked rows.
    useEffect(() => {
        const api = gridApiRef.current;
        const order = selectionOrderRef.current;
        if (!api || order.length <= selectionLimit) return;
        order.slice(selectionLimit).forEach((v) => api.getRowNode(v)?.setSelected(false));
    }, [selectionLimit]);

    const handleRememberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setRememberMe(checked);
        localStorage.setItem(storageKeys.flag, String(checked));
        if (checked && selectionOrderRef.current.length > 0) {
            localStorage.setItem(storageKeys.dms, JSON.stringify(selectionOrderRef.current));
        } else {
            localStorage.removeItem(storageKeys.dms);
        }
    };

    const handleOnOpenDMSClick = async () => {
        setIsLaunchingDrive(true);

        try {
            const selectedCNumbers = selectedDMS.map((dms) => dms.value);

            if (selectedCNumbers.length > 0) {
                const { url: dmsProxyUrl } = getDMSProxyFirewallUrl({
                    cNumber: selectedCNumbers,
                    driveToken: authToken,
                    lang: userLocale
                });

                trackEvent(AMPLITUDE_EVENTS.OPEN_DRIVE, {
                    'C-Number': selectedCNumbers.join(','),
                    Name: selectedDMS.map((dms) => dms.label).join(',')
                });

                await driveLaunchFallback(dmsProxyUrl);
            }
        } catch (e) {
            console.error('[MultipleDMSDialog] Error launching drive', e);
        } finally {
            setIsLaunchingDrive(false);
            setDialogOpen(false);
        }
    };

    return (
        <StyledDialog
            dataTestId="multiple-dms-dialog"
            id="multiple-dms-dialog"
            onClose={() => setDialogOpen(false)}
            title={<DMSDialogTitle />}
            buttonsProps={[
                {
                    id: 'multiple-dms-dialog-cancel-btn',
                    onClick: () => setDialogOpen(false),
                    text: t('common.cancel'),
                    variant: 'text'
                },
                {
                    id: 'multiple-dms-dialog-open-dms-btn',
                    onClick: handleOnOpenDMSClick,
                    text: isFetching ? t('multipleDMSDialog.fetching') : t('multipleDMSDialog.openDMS'),
                    variant: 'primary',
                    isDisabled:
                        selectedDMS.length === 0 ||
                        selectedDMS.length > selectionLimit ||
                        isLaunchingDrive
                }
            ]}
            isOpen={shouldDialogOpen}
        >
            <StyledWithLoader data-testid="multi-dms-dialog-container">
                <StyledSearchBoxContainer>
                    <StyledSearchBox
                        dataTestId="dms-search-input"
                        className="customize-search-input"
                        clearButtonLabel="clearButtonLabel"
                        id="dms-grid-search-input-expanded"
                        isAlwaysExpanded
                        name="dms-grid-search-input-expanded"
                        onChange={(v: any) =>
                            setSearchText(typeof v === 'string' ? v : v?.target?.value ?? '')
                        }
                        placeholder="Enter DMS name/number"
                        size={INPUT_SIZES.STANDARD}
                        value={searchText}
                    />

                    <Checkbox
                        checked={rememberMe}
                        label={t('multipleDMSDialog.rememberSelection')}
                        name="rememberSelection"
                        size={CHECKBOX_SIZES.STANDARD}
                        onChange={handleRememberChange}
                    />
                </StyledSearchBoxContainer>

                <TableContainer agGridVersion="30.2.0">
                    <TableHeader id="multiple-dms-table-header" hideBorder isDownloadable={false} isPrintable={false} isSearchable={false} />
                    <TableContent
                        data-testid="table-content"
                        className={darkMode ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
                        style={{ height: `${TABLE_CONTENT_HEIGHT}px` }}
                    >
                        <AgGridReact<RowData>
                            // Remount when switching between single and multiple selection.
                            key={isMulti ? 'multi' : 'single'}
                            defaultColDef={DEFAULT_COL_DEF}
                            rowHeight={ROW_HEIGHT}
                            headerHeight={HEADER_HEIGHT}
                            suppressRowClickSelection
                            rowSelection={isMulti ? 'multiple' : 'single'}
                            rowData={rowData}
                            columnDefs={columnDefs}
                            getRowId={getRowId}
                            quickFilterText={searchText}
                            onGridReady={(params: GridReadyEvent<RowData>) => {
                                gridApiRef.current = params.api;
                            }}
                            onFirstDataRendered={handleFirstDataRendered}
                            onSelectionChanged={handleSelectionChanged}
                        />
                    </TableContent>
                </TableContainer>

                {isLaunchingDrive && <Loader label="Launching Drive..." />}
            </StyledWithLoader>
        </StyledDialog>
    );
};

export default MultipleDMSDialog;